import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { translate, TranslocoModule } from '@ngneat/transloco';
import { isNil } from 'lodash';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { defer, forkJoin, from, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { SurveyCreatorModel } from 'survey-creator-core';

import { Form, FormVersion } from '@pwp-common';

import { StylesService } from '../../../ui/styles/styles.service';
import { ProgressSpinnerModule } from '../../core/progress-spinner/progress-spinner.module';
import { AuditEntryComponent } from '../../generic/settings/common/audit-entry/audit-entry.component';
import { makeAuditEntry } from '../../generic/settings/common/audit-entry/helpers/make-audit-entry';
import { SettingsModule } from '../../generic/settings/settings.module';
import { FormVersionService } from '../services/form-version.service';
import { FormService } from '../services/form.service';
import { SurveyJSCreatorModule } from '../survey-js-creator.module';
import { surveyJSTheme } from '../utils/create-survey-model/survey-js-theme';

import { allowedSurveyJSQuestionTypes } from './constants/allowed-survey-js-question-types';
import { hideSurveyCreatorPropertiesFromGrid } from './helpers/hide-survey-creator-properties-from-grid';

@Component({
  selector: 'app-form-editor',
  standalone: true,
  templateUrl: './form-editor.component.html',
  styleUrls: ['./form-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonModule,
    CommonModule,
    ConfirmDialogModule,
    SurveyJSCreatorModule,
    ProgressSpinnerModule,
    SettingsModule,
    AuditEntryComponent,
    TranslocoModule,
  ],
  providers: [ConfirmationService],
})
export class FormEditorComponent {
  //////////////////////////////////////////////////////////////////////////////////////////
  // Services
  //////////////////////////////////////////////////////////////////////////////////////////

  private readonly confirmationService = inject(ConfirmationService);

  private readonly dialogConfig = inject(DynamicDialogConfig, { optional: true });

  private readonly dialogRef = inject(DynamicDialogRef, { optional: true });

  private readonly formVersionService = inject(FormVersionService);

  private readonly formService = inject(FormService);

  private readonly stylesService = inject(StylesService);

  public readonly isInsideDialog = !isNil(this.dialogRef);

  //////////////////////////////////////////////////////////////////////////////////////////
  // Data
  //////////////////////////////////////////////////////////////////////////////////////////

  private creatorModel?: SurveyCreatorModel;

  private readonly formVersion$ = defer(() => this.getFormVersion()).pipe(
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  private readonly auditEntry$ = this.formVersion$.pipe(map((formVersion) => makeAuditEntry(formVersion)));

  private readonly creatorModel$ = this.formVersion$.pipe(map((formVersion) => this.getCreatorModel(formVersion)));

  private readonly stylesLoaded$ = from(this.stylesService.lazyLoadGlobalStyle('survey-creator')).pipe(map(() => true));

  public readonly data$ = defer(() =>
    forkJoin({
      auditEntry: this.auditEntry$,
      creatorModel: this.creatorModel$,
      stylesLoaded: this.stylesLoaded$,
    }),
  );

  //////////////////////////////////////////////////////////////////////////////////////////
  // Inputs
  //////////////////////////////////////////////////////////////////////////////////////////

  @Input() public form?: Form;

  //////////////////////////////////////////////////////////////////////////////////////////
  // Getters
  //////////////////////////////////////////////////////////////////////////////////////////

  private getFormId(): string {
    return this.form?.getId() ?? this.dialogConfig?.data.formId;
  }

  private getForm(): Observable<Form | undefined> {
    return isNil(this.form) ? this.formService.getDoc(this.getFormId()) : of(this.form);
  }

  private getFormVersion(): Observable<FormVersion | undefined> {
    return this.getForm().pipe(
      switchMap((form) => {
        if (isNil(form)) {
          return of(undefined);
        }
        return this.formVersionService.getDoc(form.getActiveVersionId());
      }),
    );
  }

  //////////////////////////////////////////////////////////////////////////////////////////
  // Getters
  //////////////////////////////////////////////////////////////////////////////////////////

  private async saveFormVersion(
    model: SurveyCreatorModel,
    updateSurveySaveState: (success: boolean) => void,
  ): Promise<void> {
    await this.formVersionService.createSurveyJSVersion(this.getFormId(), model);

    updateSurveySaveState(true);
    this.closeDialog();
  }

  //////////////////////////////////////////////////////////////////////////////////////////
  // Close Confirmation
  //////////////////////////////////////////////////////////////////////////////////////////

  protected onCloseDialogClicked(): void {
    if (this.hasUnsavedChanges()) {
      this.confirmDiscardUnsavedChanges();
    } else {
      this.closeDialog();
    }
  }

  private hasUnsavedChanges(): boolean {
    return this.creatorModel?.state === 'modified';
  }

  private confirmDiscardUnsavedChanges(): void {
    this.confirmationService.confirm({
      header: translate('form-editor.unsavedChanges.header'),
      message: translate('form-editor.unsavedChanges.message'),
      acceptLabel: translate('form-editor.unsavedChanges.acceptLabel'),
      rejectLabel: translate('form-editor.unsavedChanges.rejectLabel'),
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => this.closeDialog(),
    });
  }

  private closeDialog(): void {
    this.dialogRef?.close();
  }

  private getCreatorModel(formVersion?: FormVersion): SurveyCreatorModel {
    const model = new SurveyCreatorModel({
      showJSONEditorTab: false,
      showCreatorThemeSettings: false,
      questionTypes: allowedSurveyJSQuestionTypes,
      showLogicTab: false,
    });

    hideSurveyCreatorPropertiesFromGrid(model);
    model.applyCreatorTheme(surveyJSTheme);

    model.saveSurveyFunc = (saveNo, updateSurveySaveState) =>
      this.saveFormVersion(model, (success) => updateSurveySaveState(saveNo, success));
    model.text = formVersion?.formSchema ?? '';

    this.creatorModel = model;
    return model;
  }
}
